/* eslint-disable i18next/no-literal-string */
import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error?: [Error, ErrorInfo];
}

/**
 * Component that renders an error page if an error occurs.
 *
 * The message is shown, not just swallowed: it is the only thing a user can send back when a page
 * dies, and without it every report is "it says Whoops".
 *
 * The stack itself is only unfolded in development. In production it goes behind a copy button
 * instead — error messages in this app carry Geotab context such as device and diagnostic ids,
 * and a fleet manager's screen is often not private.
 */
export class ErrorBoundary extends Component<Props, State> {
  render() {
    const err = this.state?.error;
    if (!err) {
      return this.props.children;
    }

    const [error, errorInfo] = err;
    const details = [error.stack, errorInfo.componentStack].filter(Boolean).join("\n\n");

    return (
      <div style={{ padding: 16, fontFamily: "sans-serif" }}>
        <h1>Whoops</h1>
        <p>Something went wrong.</p>

        <p style={{ fontWeight: 600 }}>{error.message || String(error)}</p>

        {import.meta.env.DEV ? (
          <details>
            <summary>Details</summary>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, overflowX: "auto" }}>{details}</pre>
          </details>
        ) : (
          <p>
            <button onClick={() => void navigator.clipboard?.writeText(details)}>
              Copy details
            </button>
          </p>
        )}

        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Logged as well, so the browser console keeps a copy the user can copy out of.
    console.error("Add-in crashed:", error, errorInfo.componentStack);
    this.setState({ error: [error, errorInfo] });
  }
}
